import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Reveal, Overline } from "@/components/common";

// Percent counter (supports one decimal, e.g. "84.3%")
const PctCounter = ({ value, duration = 1800, className = "" }) => {
  const [display, setDisplay] = useState(0);
  const started = useRef(false);
  const start = () => {
    if (started.current) return;
    started.current = true;
    let startTs = null;
    const step = (ts) => {
      if (startTs === null) startTs = ts;
      const progress = Math.min((ts - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(eased * value);
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
  return (
    <motion.span
      className={className}
      onViewportEnter={start}
      viewport={{ once: true, margin: "-60px" }}
    >
      {display.toFixed(1)}%
    </motion.span>
  );
};

// Integer counter used in metric cards
const IntCounter = ({ value, duration = 1800, className = "" }) => {
  const [display, setDisplay] = useState(0);
  const started = useRef(false);
  const start = () => {
    if (started.current) return;
    started.current = true;
    let startTs = null;
    const step = (ts) => {
      if (startTs === null) startTs = ts;
      const progress = Math.min((ts - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * value));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
  return (
    <motion.span
      className={className}
      onViewportEnter={start}
      viewport={{ once: true, margin: "-60px" }}
    >
      {display.toLocaleString("en-US")}
    </motion.span>
  );
};

const METRIC_IDS = [
  { id: "rna",         value: 626,  subMain: null, subPct: 16.0 },
  { id: "assessments", value: 6186 },
  { id: "cities",      value: 30 },
  { id: "outreach",    value: 241 },
];

export const ExecutiveSnapshot = () => {
  const { t } = useTranslation();
  // Pull localized labels for the metric cards. The static IDs above hold
  // the numeric values; text (kicker/lead/note/subText) comes from i18n.
  const localizedMetrics = t("executiveSnapshot.metrics", { returnObjects: true });
  const METRICS = METRIC_IDS.map((m, i) => ({
    ...m,
    ...(Array.isArray(localizedMetrics) ? localizedMetrics[i] : {}),
  }));
  return (
  <section
    className="py-16 sm:py-20 bg-brand-offwhite"
    data-testid="executive-snapshot-section"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Overline className="text-brand-teal-dark">
          {t("executiveSnapshot.overlinePart1")}
          <span className="hidden md:inline"> · </span>
          <br className="md:hidden" />
          {t("executiveSnapshot.overlinePart2")}
        </Overline>
        <h2
          className="mt-3 text-3xl sm:text-4xl font-bold font-heading text-brand-navy leading-tight"
          data-testid="executive-snapshot-heading"
        >
          {t("executiveSnapshot.heading")}
        </h2>
      </Reveal>

      {/* Hero row — single navy card; linkage-to-care now lives in the
          RNA prevalence metric card below */}
      <div className="mt-8">
        {/* Navy hero card */}
        <Reveal>
          <div
            className="relative rounded-3xl bg-brand-navy text-white p-8 sm:p-10 overflow-hidden shadow-2xl shadow-brand-navy/20 h-full"
            data-testid="hero-clients-screened"
          >
            <div className="text-[11px] font-heading font-extrabold tracking-[.22em] uppercase text-white/60">
              {t("executiveSnapshot.cardClientsScreenedKicker")}
            </div>
            <div className="mt-3 font-heading font-extrabold text-[60px] sm:text-[72px] leading-none tracking-tight text-brand-cream">
              <IntCounter value={3991} />
            </div>
            <div className="mt-4 font-heading font-extrabold text-xl sm:text-2xl">
              {t("executiveSnapshot.cardClientsScreenedTitle")}
            </div>
            <p className="mt-1 text-white/75 text-sm sm:text-[15px] leading-relaxed">
              {t("executiveSnapshot.cardClientsScreenedBody")}
            </p>
            <div className="mt-6 inline-block bg-white/10 text-white text-[11px] font-bold tracking-[.14em] uppercase px-3 py-1.5 rounded-full">
              {t("executiveSnapshot.cardClientsScreenedPill")}
            </div>
          </div>
        </Reveal>
      </div>

      {/* Metric cards */}
      <div className="mt-5 grid md:grid-cols-2 gap-5">
        {METRICS.map((m, i) => (
          <Reveal key={m.id} delay={i * 0.06}>
            <div
              className="bg-white border border-slate-200 border-l-4 border-l-brand-teal rounded-2xl p-6 sm:p-8 h-full"
              data-testid={`snapshot-metric-${m.id}`}
            >
              <div className="text-[11px] font-heading font-extrabold tracking-[.18em] uppercase text-brand-teal-dark">
                {m.kicker}
              </div>
              <div className="mt-3 flex items-baseline gap-3 flex-wrap">
                <div className="font-heading font-extrabold text-5xl sm:text-[52px] leading-none tracking-tight text-brand-navy">
                  <IntCounter value={m.value} />
                </div>
                {m.subPct != null && (
                  <div className="font-heading font-bold text-base sm:text-lg text-brand-teal-dark">
                    <span className="text-slate-400/60 mx-0.5">·</span>
                    <PctCounter value={m.subPct} />
                  </div>
                )}
                {m.subText && (
                  <div className="font-heading font-bold text-base sm:text-lg text-brand-teal-dark">
                    <span className="text-slate-400/60 mx-0.5">·</span>
                    {m.subText}
                  </div>
                )}
              </div>
              <p className="mt-3 text-brand-navy font-semibold text-[15px] leading-snug whitespace-pre-line md:whitespace-normal">
                {m.lead}
              </p>
              <p className="mt-1 text-slate-500 text-sm leading-snug whitespace-pre-line md:whitespace-normal">
                {m.note}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
  );
};

export default ExecutiveSnapshot;
